import { defineComponent as _defineComponent } from 'vue'
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, unref as _unref, createVNode as _createVNode, renderSlot as _renderSlot, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "media-devices-selector" }

import type { ComponentPublicInstance } from 'vue'

import { t } from '@nextcloud/l10n'
import { computed, h } from 'vue'
import NcIconSvgWrapper from '@nextcloud/vue/components/NcIconSvgWrapper'
import NcSelect from '@nextcloud/vue/components/NcSelect'
import IconMicrophoneOutline from 'vue-material-design-icons/MicrophoneOutline.vue'
import IconVideoOutline from 'vue-material-design-icons/VideoOutline.vue'
import IconVolumeHighOutline from '../../../img/material-icons/volume-high-outline.svg?raw'

type NcSelectOption = { id: string | null, label: string }
type MediaDeviceInfoWithFallbackLabel = MediaDeviceInfo & { fallbackLabel: string }


export default /*@__PURE__*/_defineComponent({
  __name: 'MediaDevicesSelector',
  props: {
    kind: {},
    devices: {},
    deviceId: { default: undefined },
    enabled: { type: Boolean, default: true }
  },
  emits: ["refresh", "update:deviceId"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const deviceOptions = computed<NcSelectOption[]>(() => {
	const kindDevices = props.devices.filter((device) => device.kind === props.kind)
		.map((device) => ({
			id: device.deviceId,
			label: device.label ? device.label : device.fallbackLabel,
		}))
	if (props.kind === 'audiooutput') {
		return kindDevices
	}
	return [...kindDevices, { id: null, label: t('spreed', 'None') }]
})
const deviceOptionsAvailable = computed(() => deviceOptions.value.length > 1)

const deviceIcon = computed<ComponentPublicInstance | null>(() => {
	switch (props.kind) {
		case 'audioinput': return IconMicrophoneOutline
		case 'audiooutput': return h(NcIconSvgWrapper, { svg: IconVolumeHighOutline, size: 20 })
		case 'videoinput': return IconVideoOutline
		default: return null
	}
})
const deviceSelectorPlaceholder = computed(() => {
	switch (props.kind) {
		case 'audioinput': return deviceOptionsAvailable.value ? t('spreed', 'Select microphone') : t('spreed', 'No microphone available')
		case 'audiooutput': return deviceOptionsAvailable.value ? t('spreed', 'Select speaker') : t('spreed', 'No speaker available')
		case 'videoinput': return deviceOptionsAvailable.value ? t('spreed', 'Select camera') : t('spreed', 'No camera available')
		default: return ''
	}
})

const deviceSelectedOption = computed<NcSelectOption | null>({
	get: () => {
		return deviceOptions.value.find((option) => option.id === props.deviceId) ?? null
	},
	set: (value) => {
		updateDeviceId(value?.id ?? null)
	},
})

/**
 * Update deviceId if passes required checks
 *
 * @param deviceId selected NcSelect option to update with
 */
function updateDeviceId(deviceId: NcSelectOption['id']) {
	// The deviceSelectedOption may be the same as before yet a change
	// could be triggered if media permissions are granted, which would
	// update the label.
	if (deviceId === props.deviceId) {
		return
	}

	// The previous selected option changed due to the device being
	// disconnected, so ignore it as it was not explicitly changed by
	// the user.
	if (props.deviceId && !deviceOptions.value.find((option) => option.id === props.deviceId)) {
		return
	}

	// Ignore device change on initial loading of the settings dialog.
	if (typeof props.deviceId === 'undefined') {
		return
	}

	emit('update:deviceId', deviceId)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(_resolveDynamicComponent(deviceIcon.value), {
      class: "media-devices-selector__icon",
      title: "",
      size: 20
    })),
    _createVNode(_unref(NcSelect), {
      modelValue: deviceSelectedOption.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((deviceSelectedOption).value = $event)),
      "input-id": `device-selector-${props.kind}`,
      options: deviceOptions.value,
      label: "label",
      "aria-label-combobox": _unref(t)('spreed', 'Select a device'),
      clearable: false,
      placeholder: deviceSelectorPlaceholder.value,
      disabled: !__props.enabled || !deviceOptionsAvailable.value,
      onOpen: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('refresh')))
    }, null, 8, ["modelValue", "input-id", "options", "aria-label-combobox", "placeholder", "disabled"]),
    _renderSlot(_ctx.$slots, "extra-action")
  ]))
}
}

})